/**
 * Device validation functions using modular utilities
 * 
 * AI Agent Note: This module handles all device connection and power validation.
 * Uses utilities from powerCalculations for individual checks.
 */

import { Device, ValidationError, ConnectionValidation } from '../types';
import {
  checkPassthroughExceeded,
  checkPowerSourceExceeded,
  checkMissingPowerSource,
  checkTotalCurrentExceeded
} from '../utils/powerCalculations';

/**
 * Validate all device connections and power requirements
 * Returns a validation result with any issues found
 * 
 * @param devices - Array of all devices to validate
 * @returns Validation result with issues array
 */
export function validateDeviceConnections(devices: Device[]): ConnectionValidation {
  const errors: ValidationError[] = [];

  devices.forEach(device => {
    // Check if device has power source (except for power sources themselves)
    const missingPowerIssue = checkMissingPowerSource(device);
    if (missingPowerIssue) {
      errors.push(missingPowerIssue);
    }

    // Check if passthrough power is exceeded
    const passthroughIssue = checkPassthroughExceeded(device, devices);
    if (passthroughIssue) {
      errors.push(passthroughIssue);
    }

    // Check if total current limit is exceeded
    const totalCurrentIssue = checkTotalCurrentExceeded(device, devices);
    if (totalCurrentIssue) {
      errors.push(totalCurrentIssue);
    }

    // For power sources, check if total power draw exceeds capacity
    const powerSourceIssue = checkPowerSourceExceeded(device, devices);
    if (powerSourceIssue) {
      errors.push(powerSourceIssue);
    }
  });

  return {
    isValid: errors.filter(i => i.severity === 'error').length === 0,
    errors
  };
}

/**
 * Get all issues for a specific device
 * @param deviceId - ID of the device
 * @param allIssues - Array of all validation issues
 * @returns Array of issues for this device
 */
export function getDeviceIssues(deviceId: string, allIssues: ValidationError[]): ValidationError[] {
  return allIssues.filter(i => i.deviceId === deviceId);
}

/**
 * Check if a device has any errors (not just warnings)
 * @param deviceId - ID of the device
 * @param allIssues - Array of all validation issues
 * @returns true if device has errors
 */
export function hasDeviceErrors(deviceId: string, allIssues: ValidationError[]): boolean {
  return getDeviceIssues(deviceId, allIssues).some(i => i.severity === 'error');
}

/**
 * Check if a device has any warnings
 * @param deviceId - ID of the device
 * @param allIssues - Array of all validation issues
 * @returns true if device has warnings
 */
export function hasDeviceWarnings(deviceId: string, allIssues: ValidationError[]): boolean {
  return getDeviceIssues(deviceId, allIssues).some(i => i.severity === 'warning');
}
